import COLOR from "lib/colors";
import _invert from "lodash/invert";
import moment from "moment-timezone";
import { EnpsGroup } from "./types";

// If you're looking for MIN_RESPONSES type thresholds, look in:
// company-selectors getCompanySurveyGroupThreshold as it's by-company now

// Retention buffer. We do 60-day retention, so we need a 60-day buffer before
// folks are counted in our retention calculations.
export const OPPORTUNITY_RETENTION_DAYS = 60;
export const OPPORTUNITY_RETENTION_MS = OPPORTUNITY_RETENTION_DAYS * 86400 * 1000;

export const SATISFACTION_CATEGORY = "General Satisfaction";

export const IMPACT_EVENT_WINDOW_TARGET_SAMPLE = 50;
export const IMPACT_EVENT_WINDOW_CONFIDENCE_SAMPLE = 50;
export const IMPACT_EVENT_WINDOW_MIN_SAMPLE = 5;
export const IMPACT_EVENT_WINDOW_FAVORABILITY_MIN_DAYS = 30;
export const IMPACT_EVENT_WINDOW_FAVORABILITY_MAX_DAYS = 90;
// NOTE: the pre-window for turnover includes a buffer of IMPACT_EVENT_RETENTION_DAYS
export const IMPACT_EVENT_WINDOW_TURNOVER_MIN_DAYS = 30;
export const IMPACT_EVENT_WINDOW_TURNOVER_MAX_DAYS = 60;
export const IMPACT_EVENT_RETENTION_DAYS = 30;
export const IMPACT_EVENT_RETENTION_MS = IMPACT_EVENT_RETENTION_DAYS * 86400 * 1000;
export const IMPACT_EVENT_FILTER_FACILITY_KEY = "ws_facility_id";

export const AVERAGE_TENURE_WINDOW_DAYS = 90;

/**
 * When we released the "all comments" inbox and the new feature to have
 * answers marked as read or unread, we auto-marked all answers before
 * deploy date as "read".
 */
export const UNREAD_COMMENT_START_DATE = moment.utc().subtract(90, "days");

export const COMMENT_TAB = {
    OPEN: 1,
    MINE: 2,
    UNASSIGNED: 3,
    RESOLVED: 4,
    CONVERSATION: 5,
    POSITIVE: 6,
    ALL_COMMENTS: 7,
};

export const COMMENT_TAB_COUNTS_KEY = {
    [COMMENT_TAB.OPEN]: "OPEN",
    [COMMENT_TAB.MINE]: "MINE",
    [COMMENT_TAB.UNASSIGNED]: "UNASSIGNED",
    [COMMENT_TAB.RESOLVED]: "RESOLVED",
    [COMMENT_TAB.CONVERSATION]: "CONVERSATION",
    [COMMENT_TAB.POSITIVE]: "POSITIVE",
    [COMMENT_TAB.ALL_COMMENTS]: "ALL",
};

export const ALERT_ASSIGNMENT_TYPE = {
    OPEN: "OPEN",
    MINE: "MINE",
    ASSIGNED: "ASSIGNED",
    UNASSIGNED: "UNASSIGNED",
    RESOLVED: "RESOLVED",
    CONVERSATION: "CONVERSATION",
    POSITIVE: "POSITIVE",
};

export const COMMENT_TAB_ASSIGNMENT_TYPE = {
    [COMMENT_TAB.OPEN]: ALERT_ASSIGNMENT_TYPE.OPEN,
    [COMMENT_TAB.MINE]: ALERT_ASSIGNMENT_TYPE.MINE,
    [COMMENT_TAB.UNASSIGNED]: ALERT_ASSIGNMENT_TYPE.UNASSIGNED,
    [COMMENT_TAB.RESOLVED]: ALERT_ASSIGNMENT_TYPE.RESOLVED,
    [COMMENT_TAB.CONVERSATION]: ALERT_ASSIGNMENT_TYPE.CONVERSATION,
    [COMMENT_TAB.POSITIVE]: ALERT_ASSIGNMENT_TYPE.POSITIVE,
    [COMMENT_TAB.ALL_COMMENTS]: null,
};

export const COMMENT_TAB_LOOKUP = _invert(COMMENT_TAB);

// Tabs that contain only alerts
export const COMMENT_TAB_ALERTS = [
    COMMENT_TAB.OPEN,
    COMMENT_TAB.MINE,
    COMMENT_TAB.UNASSIGNED,
    COMMENT_TAB.RESOLVED,
];

export const COMMENT_PANE = {
    MENU: 1,
    LIST: 2,
    COMMENT: 3,
};

export const COMMENTS_PER_PAGE = 20;

export const OPPORTUNITIES_TABS = [
    { label: "Themes", href: "/portal/retain/opportunities/themes/" },
    { label: "Actions", href: "/portal/retain/opportunities/actions/" },
];

export const OPPORTUNITIES_DESCRIPTION =
    "The Opportunities page helps you better understand which " +
    "initiatives will have the biggest impact on your turnover " +
    "reduction. It turns data into real-time actionable insights " +
    "by highlighting the areas to focus on. Take action and watch " +
    "our action tracker to see how it affects workforce favorability " +
    "and turnover.";

export const ENPS_GROUP_LABEL = {
    detractor: {
        default: "Detractor",
        plural: "Detractors",
        full: "Detractors (0 - 6)",
    },
    passive: { default: "Passive", plural: "Passive", full: "Passive (7 and 8)" },
    promoter: {
        default: "Promoter",
        plural: "Promoters",
        full: "Promoters (9 and 10)",
    },
};

export const ENPS_GROUP_SCORE_RANGE = {
    detractor: [0, 6],
    passive: [7, 8],
    promoter: [9, 10],
};

export const ENPS_GROUP_ORDER: EnpsGroup[] = ["promoter", "passive", "detractor"];

export const ENPS_GROUP_COLOR_RESPONSE_MAP = {
    // convert the eNPS buckets to match up with the chart colors we use for rate questions
    // this is only used for chart colors.  Don't use it for anything else.
    detractor: "a1",
    passive: "a3",
    promoter: "a5",
};

export function makeEnpsScoreText(score: string | number, barLabel: string) {
    if (barLabel === "Needs Improvement") {
        return `A ${score} needs improvement`;
    }
    if (barLabel === "Good") {
        return `A score of ${score} is Good.`;
    }
    if (barLabel === "Great") {
        return `A score of ${score} is Great!`;
    }
    if (barLabel === "Excellent") {
        return `A score of ${score} is Excellent!`;
    }
    return `No eNPS yet`;
}

export const ENPS_QUALITY_RANGES = [
    {
        barLabel: "Needs Improvement",
        labelFn: makeEnpsScoreText,
        range: [-100, 0],
        color: COLOR.red,
        textColor: COLOR.white,
        valueClass: "very-negative",
        barWidth: {
            small: 136,
            default: 200,
        },
    },
    {
        barLabel: "Good",
        labelFn: makeEnpsScoreText,
        range: [0, 30],
        color: COLOR.yellow,
        textColor: COLOR["neutral-1"],
        valueClass: "medium",
        barWidth: {
            small: 48,
            default: 60,
        },
    },
    {
        barLabel: "Great",
        labelFn: makeEnpsScoreText,
        range: [30, 70],
        color: COLOR["medium-green"],
        textColor: COLOR.white,
        valueClass: "positive",
        barWidth: {
            small: 48,
            default: 70,
        },
    },
    {
        barLabel: "Excellent",
        labelFn: makeEnpsScoreText,
        range: [70, 100],
        color: COLOR.green,
        textColor: COLOR.white,
        valueClass: "very-positive",
        barWidth: {
            small: 70,
            default: 70,
        },
    },
];

export const ENPS_EMPTY_LABEL_INFO = {
    label: `No eNPS yet`,
    barLabel: "N/A",
    range: ["N/A"],
    color: COLOR["light-grey"],
    barWidth: {
        small: 302,
        default: 400,
    },
};
